import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Lock, Zap, Settings, CheckCircle2 } from "lucide-react";

export default function Upgrade() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/pricing").then((r) => setPricing(r.data));
  }, []);

  const alreadyPro = Boolean(user?.is_pro && user?.has_stripe_customer);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { origin_url: origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.status ? `Checkout failed (HTTP ${err.response.status})` : null) ||
        err?.message ||
        "Could not start checkout";
      toast.error(String(detail));
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/dashboard",
      });
      window.location.href = data.portal_url;
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not open subscription portal";
      toast.error(String(detail));
      setPortalLoading(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const expired = user && !user.has_access;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {alreadyPro ? (
          <Card className="p-8 sm:p-10 rounded-2xl bg-white border-slate-200 text-center" data-testid="already-pro-card">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold tracking-widest uppercase mb-4">
              <CheckCircle2 className="h-3 w-3" /> You&apos;re Pro
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
              Your subscription is active
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto">
              You already have full access. Manage your subscription, update your card, or cancel anytime in the Stripe portal.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                type="button"
                onClick={openBillingPortal}
                disabled={portalLoading}
                className="bg-slate-900 hover:bg-slate-800 h-11"
                data-testid="already-pro-portal-button"
              >
                <Settings className="mr-2 h-4 w-4" />
                {portalLoading ? "Opening…" : "Manage subscription"}
              </Button>
              <Link to="/dashboard" data-testid="already-pro-dashboard-link">
                <Button variant="outline" className="h-11">Back to dashboard</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <>
            {expired ? (
              <Card className="p-6 sm:p-8 rounded-2xl border-amber-200 bg-amber-50 mb-8">
                <div className="flex items-start gap-3">
                  <Lock className="h-5 w-5 text-amber-600 mt-1" />
                  <div>
                    <h2 className="font-display font-semibold text-xl text-amber-900">Your trial has ended</h2>
                    <p className="mt-1 text-amber-800 text-sm">Upgrade to keep your data, signals, and AI insights.</p>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="text-center mb-8">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold tracking-widest uppercase mb-4">
                  <Sparkles className="h-3 w-3" /> Pro plan
                </span>
                <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-slate-900">
                  Keep scaling with clarity
                </h1>
                <p className="mt-3 text-slate-600">Pro unlocks AI weekly insights, full trend charts, and scaling confidence.</p>
              </div>
            )}

        {pricing && (
          <Card className="p-8 sm:p-10 rounded-2xl bg-white border-slate-200">
            {pricing.is_founding && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-bold tracking-widest uppercase mb-4">
                <Zap className="h-3 w-3" /> Founding member — {pricing.spots_left} of {pricing.founding_limit} spots left
              </div>
            )}
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-display font-bold text-6xl text-slate-900" data-testid="pricing-current-price">
                ${Number.isInteger(pricing.current_price) ? pricing.current_price : pricing.current_price.toFixed(2)}
              </span>
              <span className="text-slate-500 text-lg">/ month</span>
              {pricing.is_founding && (
                <span className="ml-3 text-base text-slate-400 line-through" data-testid="pricing-regular-strike">
                  ${pricing.regular_price}/mo
                </span>
              )}
            </div>
            {pricing.is_founding && (
              <p className="text-sm font-semibold text-emerald-700 mt-2" data-testid="pricing-founding-line">
                Founding member price · save ${(pricing.regular_price - pricing.current_price).toFixed(2)}/mo for life
              </p>
            )}
            <p className="text-sm text-slate-500 mt-2">
              {pricing.is_founding
                ? `Lock this rate forever as one of our first ${pricing.founding_limit} customers.`
                : "Founding spots are gone — but Pro still pays for itself."}
            </p>

            <ul className="mt-8 space-y-3">
              {[
                "Unlimited weekly logs and history",
                "Full ROAS trend chart (unlocked)",
                "Scale / Hold / Stop signal with 3-week trend",
                "AI weekly insight from Claude",
                "Best/worst week + scaling confidence score",
                "Cancel any time",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-700">
                  <Check className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Button
              type="button"
              onClick={startCheckout}
              disabled={loading}
              className="mt-8 w-full h-12 bg-slate-900 hover:bg-slate-800"
              data-testid="upgrade-checkout-button"
            >
              {loading
                ? "Redirecting…"
                : `Upgrade for $${Number.isInteger(pricing.current_price) ? pricing.current_price : pricing.current_price.toFixed(2)}/month`}
            </Button>

            {expired && (
              <button
                onClick={onLogout}
                className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600"
                data-testid="upgrade-logout-link"
              >
                Or sign out
              </button>
            )}
          </Card>
        )}
          </>
        )}
      </main>
    </div>
  );
}
